import { Add, Settings } from 'almanac-ui/icons'
import cx from 'classnames'
import { useProseStateContext } from 'common/ProseInput/ProseStateContext'
import { useCallback, useMemo, useRef } from 'react'
import { KnowledgeBaseSnippet, KnowledgeBaseSnippetKind } from 'types'
import { SnippetsDropdown } from '../SnippetsDropdown/SnippetsDropdown'
import Styles from './SnippetsToolbar.module.scss'
import { generatePath } from 'react-router-dom'
import { BRAND_KITS_PATH } from 'paths'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useSnippets, useUpdateSnippetUsage } from 'hooks/useSnippets'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useOpenNewSnippetModal } from 'hooks/useOpenNewSnippetModal'
import { CreateKnowledgeBaseSnippetPayload } from 'api/clients/KnowledgeBaseClient'
import { Button } from 'blaze-ui/Button'
import { IconButton } from 'blaze-ui/IconButton/IconButton'

const byKinds = (kinds?: KnowledgeBaseSnippetKind[]) => (snippet: KnowledgeBaseSnippet) =>
  !kinds || kinds.includes(snippet.kind)

const SUGGESTED_SNIPPETS_COUNT = 3

export const SnippetsProseToolbar = ({
  kinds,
  shown = true,
}: {
  kinds?: KnowledgeBaseSnippetKind[]
  shown?: boolean
}) => {
  const toolbarRef = useRef<HTMLDivElement>(null)
  const state = useProseStateContext()
  const snippetsQuery = useSnippets()
  const workspaceId = useCurrentWorkspaceId()
  const openNewSnippetModal = useOpenNewSnippetModal()
  const updateSnippetUsage = useUpdateSnippetUsage()

  const snippets = useMemo(
    () => snippetsQuery.data?.sort((a, b) => b.use_count - a.use_count)?.filter(byKinds(kinds)),
    [kinds, snippetsQuery.data],
  )
  const noSnippets = snippetsQuery.isFetched && (snippets?.length || 0) === 0
  const suggestedSnippets = useMemo(() => snippets?.slice(0, SUGGESTED_SNIPPETS_COUNT), [snippets])
  const moreSnippets = useMemo(() => snippets?.slice(SUGGESTED_SNIPPETS_COUNT), [snippets])

  const onSelect = useCallback(
    (snippet: KnowledgeBaseSnippet) => {
      if (!state || !state.view) return
      const view = state.view
      view.dispatch(view.state.tr.insertText(snippet.content).setMeta('insertedFromToolbar', true))
      view.focus()
      updateSnippetUsage.mutate(snippet)
    },
    [state, updateSnippetUsage],
  )

  const onSave = useCallback(
    (payload: CreateKnowledgeBaseSnippetPayload) => {
      if (!state || !state.view) return
      const view = state.view
      view.dispatch(view.state.tr.insertText(payload.content).setMeta('insertedFromToolbar', true))
      view.focus()
    },
    [state],
  )

  const openSnippetModal = useCallback(() => {
    const nodes = state?.view?.state?.doc?.content?.content?.[0].content?.toJSON()
    // TODO: Improve this. It's not the cleanest way to transpile pills content into the new snippet modal
    const content = nodes?.map((node: any) => node['text'] || node['attrs']['text']).join(',')
    openNewSnippetModal(
      {
        onClose: () => state?.view?.focus(),
        onSave,
        content,
        kinds,
      },
      { newStack: true },
    )
  }, [kinds, openNewSnippetModal, state?.view, onSave])

  return (
    <div className={cx(Styles.toolbar, { [Styles.shown]: shown })} ref={toolbarRef}>
      <div className={Styles.suggestions}>
        {noSnippets ? (
          <Button variant="ghost" frontIcon={Add} size="xs" onPress={openSnippetModal}>
            Create a snippet
          </Button>
        ) : (
          <IconButton icon={Add} variant="ghost" size="xs" onPress={openSnippetModal} />
        )}
        {suggestedSnippets?.map((snippet) => (
          <Button variant="ghost" size="xs" key={snippet.id} onPress={() => onSelect(snippet)}>
            {snippet.name}
          </Button>
        ))}
        {moreSnippets && moreSnippets.length > 0 && (
          <SnippetsDropdown onSelect={onSelect} kinds={kinds} snippets={moreSnippets} />
        )}
      </div>
      <div className={Styles.rightActions}>
        <Tooltip label="Take me to the BrandKit page">
          <div>
            <IconButton
              icon={Settings}
              size="xs"
              variant="ghost"
              onPress={() => window.open(generatePath(BRAND_KITS_PATH, { workspaceId }), '_blank')}
            />
          </div>
        </Tooltip>
      </div>
    </div>
  )
}
